import { formatGBP } from '../money';
import { addDays, isoWeekday } from './time';

// ---------------------------------------------------------------- (7) premium pay
export type PremiumType = 'multiplier' | 'per_hour' | 'flat';
export interface PayPremium { premium_type?: PremiumType | null; premium_value?: number | string | null }

export interface ShiftPay {
  base_pence: number | null;       // paid hours x base rate (null when no base rate is known)
  premium_pence: number;           // extra on top of base
  total_pence: number | null;      // base + premium (null when the base is unknown and needed)
  effective_rate_pence: number | null;
  flat_pence: number;
}

/**
 * Cost of one shift given paid minutes, the base hourly rate (employee rate, else position default)
 * and the shift's premium. Multiplier premiums need a base rate; per-hour and flat premiums don't.
 */
export function shiftPay(paidMinutes: number, baseRatePence: number | null, p: PayPremium): ShiftPay {
  const hours = Math.max(0, paidMinutes) / 60;
  const value = p.premium_value == null ? 0 : Number(p.premium_value);
  const base = baseRatePence == null ? null : Math.round(hours * baseRatePence);
  let premium = 0;
  let rate = baseRatePence;
  let flat = 0;
  if (p.premium_type === 'multiplier') {
    if (baseRatePence != null) { rate = Math.round(baseRatePence * value); premium = Math.round(hours * rate) - (base ?? 0); }
  } else if (p.premium_type === 'per_hour') {
    premium = Math.round(hours * value);
    rate = baseRatePence == null ? null : baseRatePence + value;
  } else if (p.premium_type === 'flat') {
    flat = Math.round(value);
    premium = flat;
  }
  return { base_pence: base, premium_pence: premium, total_pence: base == null ? (p.premium_type === 'multiplier' ? null : premium) : base + premium, effective_rate_pence: rate, flat_pence: flat };
}

/** t689u: does the shift carry real premium pay? (A 1× multiplier or a £0 extra/bonus is none.) */
export function hasPayPremium(p: Partial<PayPremium> | null | undefined): boolean {
  if (!p?.premium_type || p.premium_value == null || p.premium_value === ('' as never)) return false;
  const v = Number(p.premium_value);
  if (!Number.isFinite(v)) return false;
  return p.premium_type === 'multiplier' ? v > 1 : v > 0;
}

export function describePremium(p: PayPremium): string | null {
  if (!hasPayPremium(p)) return null;
  const v = Number(p.premium_value);
  if (p.premium_type === 'multiplier') return `${v}× pay`;
  if (p.premium_type === 'per_hour') return `+${formatGBP(v)}/h`;
  return `+${formatGBP(v)} bonus`;
}

// ---------------------------------------------------------------- (10) recurring series
export type SeriesFrequency = 'daily' | 'weekly' | 'fortnightly';
export interface SeriesPattern { frequency: SeriesFrequency; weekdays: number[]; start_date: string; end_date: string }

const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

/**
 * Local dates of a series. Fortnightly counts weeks (Monday-based) from the week containing
 * start_date: weeks 0, 2, 4... are "on". Capped at 366 days.
 */
export function seriesDates(p: SeriesPattern): string[] {
  const out: string[] = [];
  const anchor = addDays(p.start_date, 1 - isoWeekday(p.start_date)); // Monday of first week
  const wd = new Set(p.weekdays);
  for (let d = p.start_date, i = 0; d <= p.end_date && i <= 366; d = addDays(d, 1), i++) {
    if (p.frequency === 'daily') { out.push(d); continue; }
    if (!wd.has(isoWeekday(d))) continue;
    if (p.frequency === 'fortnightly' && Math.floor(daysBetween(anchor, d) / 7) % 2 === 1) continue;
    out.push(d);
  }
  return out;
}

// ---------------------------------------------------------------- (9) monthly templates
/** Date for day-of-month `day` in the month starting `monthStart` (yyyy-mm-01), or null if the month is shorter. */
export function monthDay(monthStart: string, day: number): string | null {
  const d = addDays(monthStart, day - 1);
  return d.slice(0, 7) === monthStart.slice(0, 7) ? d : null;
}

// ---------------------------------------------------------------- (11) bank holidays
export type BankHolidayDivision = 'england-and-wales' | 'scotland' | 'northern-ireland';
interface GovUkFeed { [division: string]: { division: string; events: { title: string; date: string; notes?: string }[] } }

/** Parses https://www.gov.uk/bank-holidays.json for one division, optionally limited to a date range. */
export function parseBankHolidays(feed: unknown, division: BankHolidayDivision, from?: string, to?: string): { date: string; name: string }[] {
  const d = (feed as GovUkFeed)?.[division];
  if (!d || !Array.isArray(d.events)) throw new Error(`Bank holiday feed has no "${division}" division`);
  return d.events
    .filter(e => /^\d{4}-\d{2}-\d{2}$/.test(e.date) && typeof e.title === 'string')
    .filter(e => (!from || e.date >= from) && (!to || e.date <= to))
    .map(e => ({ date: e.date, name: e.title.slice(0, 120) }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
